import { Box, Divider, Tab as MuiTab, Tabs, TabsProps, IconButton, Menu, MenuItem, Stack } from '@mui/material'
import React, { useState } from 'react'
import { Tab } from '../../../types'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'

type BasicTabsProps = {
  tabs: Tab[]
  value: number
  handleChange: (_event: React.SyntheticEvent, newValue: number) => void
  children: React.ReactNode
  hideDivider?: boolean
  onAddEvent?: (eventType: 'PICKUP' | 'DELIVERY') => void
  /** Optional close control rendered on the right of the tab row. */
  onClose?: () => void
  /** When true, tab panels scroll inside the tabs shell so tabs stay visible. */
  scrollableContent?: boolean
  /**
   * When true, tab content fills remaining height (overflow hidden).
   * Use when a child (e.g. order form|tracking split) must stretch to the Paper bottom.
   */
  fillContent?: boolean
} & TabsProps

const BasicTabs = ({
  tabs,
  value,
  handleChange,
  hideDivider = false,
  children,
  onAddEvent,
  onClose,
  scrollableContent = false,
  fillContent = false,
}: BasicTabsProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleMenuItemClick = (eventType: 'PICKUP' | 'DELIVERY') => {
    onAddEvent?.(eventType)
    handleClose()
  }

  return (
    <Box
      sx={{
        flex: '1 1 0%',
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 9,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
          <Tabs
            value={value}
            onChange={handleChange}
            variant="scrollable"
            sx={{
              flex: 1,
              '& .MuiTabs-scrollButtons': {
                display: 'none',
              },
              '& .Mui-selected': {
                fontWeight: 700,
              },
            }}
          >
            {tabs.map((tab) => (
              <MuiTab
                key={tab.label}
                label={tab.label}
                disabled={tab.disabled}
                sx={{
                  textTransform: 'capitalize',
                  display: 'flex',
                  justifyContent: 'space-between',
                  '&.Mui-disabled': {
                    opacity: 0.45,
                  },
                }}
              />
            ))}
          </Tabs>
          {onAddEvent && (
            <>
              <IconButton
                onClick={handleClick}
                sx={{
                  color: 'primary.main',
                  marginRight: 1,
                }}
                size="small"
              >
                <AddIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={() => handleMenuItemClick('PICKUP')}>
                  Pickup
                </MenuItem>
                <MenuItem onClick={() => handleMenuItemClick('DELIVERY')}>
                  Delivery
                </MenuItem>
              </Menu>
            </>
          )}
          {onClose && (
            <IconButton
              aria-label="Close"
              onClick={onClose}
              size="small"
              sx={{
                color: 'error.main',
                mr: 1,
                '&:hover': { backgroundColor: 'error.50' },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Stack>
        {!hideDivider && (
          <Divider sx={{ border: 0.6, color: 'secondary.main' }} />
        )}
      </Box>
      <Box
        sx={{
          flex: '1 1 0%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          ...(scrollableContent
            ? {
                overflow: 'auto',
                overscrollBehaviorY: 'contain',
              }
            : fillContent
              ? { overflow: 'hidden' }
              : {}),
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

export default BasicTabs
